import React, { useEffect, useState } from 'react'
import { Avatar, Col, DatePicker, Form, FormInstance, Input, Row, Select, TimePicker } from 'antd'
import { Trash } from '@phosphor-icons/react'
import moment from 'moment'
import { leagueEventStore } from 'state/league/leagueEvent'
import { myStore } from 'state/store'
import { useGetLeagueTeam } from 'api/Team/LeagueTeam'
import { useGetOrgVenues } from 'api/EventCreation/EventCreation'
import { useCreateTeamLeagueConstraint } from 'api/league/LeagueCreation/leagueTeamConstraints'
import { convertTimeToUtcMinutes, getNameSignature } from 'helper/utility'
import { SplitedWeekDays } from 'types/globalTypes'
import { LeagueTeamResp } from 'types/team/leagueTeam'
import { OrganizationVenues } from 'types/organization'

const DAYS: { label: string; value: SplitedWeekDays }[] = [
  { label: 'Monday', value: 'Mon' },
  { label: 'Tuesday', value: 'Tue' },
  { label: 'Wednesday', value: 'Wed' },
  { label: 'Thursday', value: 'Thu' },
  { label: 'Friday', value: 'Fri' },
  { label: 'Saturday', value: 'Sat' },
  { label: 'Sunday', value: 'Sun' },
]

interface AddConstraintFormProps {
  form: FormInstance
}

const AddConstraintForm: React.FC<AddConstraintFormProps> = ({ form }) => {
  const { leagueData, setAddTeamConstraintModal } = leagueEventStore(store => ({
    leagueData: store.leagueData,
    setAddTeamConstraintModal: store.setAddTeamConstraintModal,
  }))
  const setModalBtnLoader = myStore(store => store.setModalBtnLoader)

  const [selectedDays, setSelectedDays] = useState<SplitedWeekDays[]>([])
  const [specificDates, setSpecificDates] = useState<string[]>([])

  const { data: leagueTeams, isLoading: teamsLoading, refetch: refetchTeams } =
    useGetLeagueTeam(leagueData._id)

  const { data: venues, isLoading: venuesLoading, refetch: refetchVenues } =
    useGetOrgVenues({
      organizationId: leagueData.organizationId,
      longitude: leagueData.location?.coordinates?.longitude,
      latitude: leagueData.location?.coordinates?.latitude,
    })

  const { mutate: createConstraint } = useCreateTeamLeagueConstraint()

  useEffect(() => {
    refetchTeams()
    refetchVenues()
  }, [])

  const toggleDay = (day: SplitedWeekDays) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day],
    )
  }

  const addDate = (date: moment.Moment | null) => {
    if (!date) return
    const iso = date.startOf('day').toISOString()
    if (!specificDates.includes(iso)) {
      setSpecificDates(prev => [...prev, iso])
    }
  }

  const removeDate = (date: string) => {
    setSpecificDates(prev => prev.filter(d => d !== date))
  }

  const onFinish = (values: { teamId: string; venueId: string; startTime?: any; endTime?: any; reason?: string }) => {
    if (!values.teamId) return

    const timings =
      values.startTime && values.endTime
        ? {
          startTime: convertTimeToUtcMinutes(values.startTime),
          endTime: convertTimeToUtcMinutes(values.endTime),
        }
        : undefined

    const payload = {
      teamId: values.teamId,
      organizationId: leagueData.organizationId,
      days: selectedDays,
      dates: specificDates,
      timings,
      reason: values.reason || undefined,
    }

    setModalBtnLoader(true)
    createConstraint(
      { leagueId: leagueData._id, payload },
      {
        onSuccess: () => {
          setModalBtnLoader(false)
          setAddTeamConstraintModal(false)
          form.resetFields()
          setSelectedDays([])
          setSpecificDates([])
        },
        onError: () => {
          setModalBtnLoader(false)
        },
      },
    )
  }

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <label className="input-label">Team</label>
      <Form.Item
        name="teamId"
        rules={[{ required: true, message: 'Please select a team' }]}
        className="mt-10px"
      >
        <Select
          className="h-50px select-field"
          placeholder="Select team"
          loading={teamsLoading}
        >
          {leagueTeams?.map((lt: LeagueTeamResp) => (
            <Select.Option key={lt.teamId} value={lt.teamId}>
              <div className="flex align-center gap-10px">
                <Avatar size={24} src={lt.team?.profilePhoto?.thumb}>
                  {getNameSignature(lt.team?.name || '')}
                </Avatar>
                <span>{lt.team?.name}</span>
              </div>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <div className="mt-20px">
        <label className="input-label">Weekly constraints</label>
        <div className="constraint-days-list mt-10px">
          {DAYS.map(({ label, value }) => (
            <div
              key={value}
              className="constraint-day-row"
              onClick={() => toggleDay(value)}
            >
              <div className={`constraint-checkbox ${selectedDays.includes(value) ? 'checked' : ''}`} />
              <span className="text-14 w-500">{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-24px">
        <div className="flex align-center justify-between mb-10px">
          <label className="input-label">Specific dates</label>
          <DatePicker
            className="constraint-date-picker"
            placeholder="Add date"
            onChange={addDate}
            value={null}
            disabledDate={current =>
              specificDates.includes(current.startOf('day').toISOString())
            }
          />
        </div>
        {specificDates.length > 0 && (
          <div className="constraint-dates-list">
            {specificDates.map(date => (
              <div key={date} className="constraint-date-tag">
                <span className="text-13">{moment(date).utc().format('MMM DD, YYYY')}</span>
                <button
                  type="button"
                  className="constraint-date-remove"
                  onClick={() => removeDate(date)}
                >
                  <Trash size={14} color="#e05151" />
                </button>
              </div>
            ))}
          </div>
        )}
        {specificDates.length === 0 && (
          <p className="text-gray text-13">No specific dates added</p>
        )}
      </div>

      <div className="mt-20px">
        <label className="input-label">Time range (optional)</label>
        <Row gutter={12} className="mt-10px">
          <Col span={12}>
            <label className="text-13 text-gray mb-6px" style={{ display: 'block' }}>Start time</label>
            <Form.Item name="startTime" className="mb-0">
              <TimePicker
                className="w-100 h-50px"
                format="hh:mm A"
                use12Hours
                placeholder="Start"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label className="text-13 text-gray mb-6px" style={{ display: 'block' }}>End time</label>
            <Form.Item name="endTime" className="mb-0">
              <TimePicker
                className="w-100 h-50px"
                format="hh:mm A"
                use12Hours
                placeholder="End"
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className="mt-20px">
        <label className="input-label">Reason (optional)</label>
        <Form.Item name="reason" className="mt-10px mb-0">
          <Input
            className="h-50px"
            placeholder="e.g. Venue conflict, team schedule"
          />
        </Form.Item>
      </div>
    </Form>
  )
}

export default AddConstraintForm
